import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import React from 'react'
import { BillType, ORDER_TYPE, TAKEAWAY_STATUS } from '../../../all-types'
import { CommonBtn } from '../../../components'
import { deliveryOptions } from '../../../constants'
import { updateTakeawayBillStatus } from '../../../services'
import { BillPrinter } from '../../printer'

const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.78rem',
  height: 28,
  fontWeight: 900,
  textTransform: 'capitalize',
  borderRadius: 8,
}))

const OrderTypeBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  fontSize: '0.75rem',
  fontWeight: 'medium',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
}))

type TakeawayCardProps = {
  order: BillType
  onStatusChange?: (id: string, status: TAKEAWAY_STATUS | 'DONE') => void
}

const TakeawayCard: React.FC<TakeawayCardProps> = ({
  order,
  onStatusChange,
}) => {
  const theme = useTheme()

  const getTypeTextColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.freeTable.main
      case ORDER_TYPE.TAKEAWAY:
        return '#E7AA5A'
      default:
        return 'default'
    }
  }

  const getTypeBorderColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return '#ADEED8'
      case ORDER_TYPE.TAKEAWAY:
        return '#FFE6BB'
      default:
        return 'default'
    }
  }

  const getTypeColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.color8.main
      case ORDER_TYPE.TAKEAWAY:
        return '#FFF9ED'
      default:
        return 'default'
    }
  }

  const getInstructions = (inst: string) => {
    try {
      const instructions: { [key: string]: string } = JSON.parse(inst)
      return (
        Object.entries(instructions)
          ?.map(([key, value]) => `${key}: ${value}`)
          .join(', ') || ''
      )
    } catch {
      return ''
    }
  }

  if (!order) return null

  const getTimeSinceOrder = () => {
    const orderTime = Number(order?.timeInfo?.timestamp || 0)
    const now = Date.now()
    const diffInMinutes = Math.max(
      0,
      Math.floor((now - orderTime) / (1000 * 60))
    )
    return diffInMinutes
  }

  const getTimeSinceOrderLabel = () => {
    const minutes = getTimeSinceOrder()
    if (minutes < 1) return 'Now'
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    if (remainingMinutes === 0) return `${hours} hr`
    return `${hours} hr ${remainingMinutes} min`
  }

  const handleUpdate = async (status: TAKEAWAY_STATUS | 'DONE') => {
    try {
      if (order._id) {
        await updateTakeawayBillStatus(order._id, status)
        onStatusChange?.(order._id, status)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const isPending = order.takeAwayStatus === 'PENDING'
  const isReady = order.takeAwayStatus === 'READY'
  const statusColor = isReady ? '#16A34A' : '#EA580C'
  const statusBgColor = isReady ? '#DCFCE7' : '#FFEDD5'
  const paymentColor = order.modeOfPayment === 'CASH' ? '#A16207' : '#15803D'
  const paymentBgColor = order.modeOfPayment === 'CASH' ? '#FEF3C7' : '#DCFCE7'

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card
        elevation={4}
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRadius: 4,
          background: isReady
            ? 'linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 56%)'
            : 'linear-gradient(180deg, #FFF7ED 0%, #FFFFFF 56%)',
          border: `2px solid ${statusColor}`,
          overflow: 'hidden',
          '&:hover': { boxShadow: 5 },
        }}
      >
        <Box sx={{ height: 6, bgcolor: statusColor }} />
        <CardContent sx={{ flexGrow: 1, p: 2.25 }}>
          <Stack spacing={1.25}>
            {/* Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <img
                  src={
                    deliveryOptions[order?.takeAwayType || 'Take-Away/Parcel']
                      ?.logoUrl
                  }
                  alt={order?.takeAwayType}
                  style={{ width: 42, height: 42, borderRadius: 10 }}
                />
                <Box>
                  <Typography variant="h6" fontWeight={900} lineHeight={1.1}>
                    {order?.takeAwayType}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    color="text.secondary"
                  >
                    Order #{order?.sNo}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1}>
                <StatusChip
                  label={(order.takeAwayStatus || 'PENDING').toLowerCase()}
                  sx={{
                    bgcolor: statusBgColor,
                    color: statusColor,
                    border: `1px solid ${statusColor}`,
                  }}
                />
                <StatusChip
                  label={order.modeOfPayment === 'CASH' ? 'Cash' : 'Paid'}
                  sx={{
                    bgcolor: paymentBgColor,
                    color: paymentColor,
                    border: `1px solid ${paymentColor}`,
                  }}
                />
              </Stack>
            </Stack>

            {/* Meta */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: statusBgColor,
                border: `1px solid ${statusColor}`,
              }}
            >
              <Typography variant="body2" fontWeight={900} color={statusColor}>
                {isReady ? 'Ready for pickup' : 'Waiting in kitchen'}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: '#FFFFFF',
                  color: statusColor,
                  boxShadow: 1,
                }}
              >
                <AccessTimeIcon fontSize="small" />
                <Typography variant="h6" fontWeight={900} lineHeight={1}>
                  {getTimeSinceOrderLabel()}
                </Typography>
              </Stack>
            </Stack>

            <Divider />

            {/* Items */}
            <Stack spacing={1}>
              {order.items?.map((item: any, index) => (
                <Stack
                  key={item._id || `${index}-${item.itemName}`}
                  spacing={0.5}
                >
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={800}>
                      {index + 1}• {item.itemName} (x {item.noOfItems})
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      color="text.secondary"
                    >
                      {item.quantity <= 1 ? '' : item.quantity} (
                      {item.quantityType})
                    </Typography>
                  </Stack>
                  {!!item?.kitchInfo?.kitchenInstrcutions && (
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      {getInstructions(item?.kitchInfo?.kitchenInstrcutions)}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>

            <Divider />

            <Typography variant="subtitle1" fontWeight={900}>
              Amount:{' '}
              <Box component="span" fontWeight="bold">
                ₹{order.billingInfo?.finalBillAmount || 0}
              </Box>
            </Typography>
          </Stack>
        </CardContent>

        {/* Footer actions: single row */}
        <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
          {/* Print button (always) */}
          <Box>
            <BillPrinter bill={order} takeawayKitchenPrint />
          </Box>

          {/* State action (right) */}
          {isPending && (
            <CommonBtn
              variant="contained"
              endIcon={<CheckCircleIcon />}
              onClick={() => handleUpdate(TAKEAWAY_STATUS.READY)}
            >
              Mark as Ready
            </CommonBtn>
          )}
          {isReady && (
            <CommonBtn
              variant="contained"
              color="success"
              endIcon={<DoneAllIcon />}
              onClick={() => handleUpdate('DONE')}
            >
              Mark as Complete
            </CommonBtn>
          )}
        </CardActions>

        <OrderTypeBadge
          sx={{
            bgcolor: `${getTypeColor(order.orderType)}`,
            color: `${getTypeTextColor(order.orderType)}`,
            borderTop: `1px dashed ${getTypeBorderColor(order.orderType)}`,
            py: 1.25,
          }}
        >
          <Typography
            variant="body2"
            textTransform="capitalize"
            fontWeight={900}
          >
            {order.orderType.toLowerCase()}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              color: statusColor,
            }}
          >
            <AccessTimeIcon fontSize="small" />
            <Typography variant="body2" fontWeight={900}>
              {getTimeSinceOrderLabel()}
            </Typography>
          </Box>
        </OrderTypeBadge>
      </Card>
    </Grid>
  )
}

export { TakeawayCard }
